import { CheckIcon, CopyIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createHighlighterCore, type HighlighterCore } from 'shiki/core';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';
import { uiText } from '../i18n.js';
import { copyTextToClipboard } from '../lib/client-runtime.js';
import { cn } from '../lib/utils.js';
import { Button } from './button.js';
import { toast } from './toast.js';

/* Highlighter fine-grained (ADR-0552, estendido em ADR-0722): engine JavaScript
   (sem WASM/oniguruma), github-light/dark carregados uma vez. As LINGUAGENS são
   lazy-loaded sob demanda (não o bundle inteiro) — o singleton nasce sem nenhuma
   lang e cada bloco carrega só a sua via `ensureLanguage`. NÃO chamar uiText em
   escopo de módulo. */
let highlighterPromise: Promise<HighlighterCore> | undefined;
function getHighlighter(): Promise<HighlighterCore> {
  highlighterPromise ??= createHighlighterCore({
    themes: [import('@shikijs/themes/github-light'), import('@shikijs/themes/github-dark')],
    langs: [],
    engine: createJavaScriptRegexEngine(),
  });
  return highlighterPromise;
}

/* Aliases da classe `language-*` do markdown → id shiki. Cobre o conjunto usado
   nas docs de packs/app; qualquer coisa fora do mapa cai no fallback cru (sem
   highlight, ainda com botão copiar). */
const LANG_ALIASES: Readonly<Record<string, string>> = {
  ts: 'typescript',
  typescript: 'typescript',
  mts: 'typescript',
  cts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  javascript: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  node: 'javascript',
  jsx: 'jsx',
  json: 'json',
  jsonc: 'json',
  json5: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  bash: 'bash',
  sh: 'bash',
  shell: 'bash',
  shellscript: 'bash',
  zsh: 'bash',
  go: 'go',
  golang: 'go',
  python: 'python',
  py: 'python',
  prisma: 'prisma',
  sql: 'sql',
  env: 'dotenv',
  dotenv: 'dotenv',
  md: 'markdown',
  markdown: 'markdown',
  http: 'http',
};

/* Loader dinâmico por id shiki — cada import vira um chunk separado (Vite) e só é
   baixado quando um bloco daquela linguagem aparece. */
type LangLoader = () => Promise<typeof import('@shikijs/langs/yaml')>;
const LANG_LOADERS: Readonly<Record<string, LangLoader>> = {
  typescript: () => import('@shikijs/langs/typescript'),
  tsx: () => import('@shikijs/langs/tsx'),
  javascript: () => import('@shikijs/langs/javascript'),
  jsx: () => import('@shikijs/langs/jsx'),
  json: () => import('@shikijs/langs/json'),
  yaml: () => import('@shikijs/langs/yaml'),
  bash: () => import('@shikijs/langs/bash'),
  go: () => import('@shikijs/langs/go'),
  python: () => import('@shikijs/langs/python'),
  prisma: () => import('@shikijs/langs/prisma'),
  sql: () => import('@shikijs/langs/sql'),
  dotenv: () => import('@shikijs/langs/dotenv'),
  markdown: () => import('@shikijs/langs/markdown'),
  http: () => import('@shikijs/langs/http'),
};

/** Mapeia a linguagem crua do fence para um id shiki suportado (ou undefined). */
export function resolveShikiLang(language: string): string | undefined {
  return LANG_ALIASES[language.trim().toLowerCase()];
}

/* Dedup de carregamento por linguagem: blocos concorrentes da mesma lang
   compartilham a mesma promise de load (evita loadLanguage duplicado). */
const langLoads = new Map<string, Promise<void>>();
function ensureLanguage(highlighter: HighlighterCore, langId: string): Promise<void> {
  const inflight = langLoads.get(langId);
  if (inflight !== undefined) return inflight;
  const loader = LANG_LOADERS[langId];
  if (loader === undefined) return Promise.resolve();
  const load = highlighter.loadLanguage(loader()).then(() => undefined);
  langLoads.set(langId, load);
  return load;
}

const COPIED_RESET_MS = 2000;

interface CodeBlockProps {
  /** Código a exibir (literal técnico — não passa por i18n). */
  readonly code: string;
  /** Linguagem para o highlight (ex.: 'ts', 'bash', 'prisma'). */
  readonly language: string;
  readonly className?: string | undefined;
}

/**
 * Bloco de código com syntax highlight (shiki, lazy por linguagem) + botão de
 * copiar. Enquanto o highlighter/linguagem assíncronos não resolvem — ou quando
 * a linguagem não é suportada — renderiza o código cru (fallback acessível e
 * SSR/erro-safe), nunca deixando a área vazia.
 */
export function CodeBlock({ code, language, className }: CodeBlockProps): React.JSX.Element {
  const shikiLang = resolveShikiLang(language);
  const [html, setHtml] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (shikiLang === undefined) {
      setHtml(null);
      return;
    }
    let active = true;
    getHighlighter()
      .then((h) => ensureLanguage(h, shikiLang).then(() => h))
      .then((h) =>
        h.codeToHtml(code, {
          lang: shikiLang,
          themes: { light: 'github-light', dark: 'github-dark' },
          defaultColor: false,
        }),
      )
      .then((out) => {
        if (active) setHtml(out);
      })
      .catch(() => {
        /* Highlight falhou (lang ausente, parse) → mantém o fallback cru. */
      });
    return () => {
      active = false;
    };
  }, [code, shikiLang]);

  useEffect(
    () => () => {
      if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
    },
    [],
  );

  function handleCopy(): void {
    copyTextToClipboard(code).then((didCopy) => {
      if (didCopy) {
        setCopied(true);
        toast.success(uiText('common.copy_success'));
        if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
      } else {
        toast.error(uiText('common.copy_error'));
      }
    });
  }

  const preClass = cn(
    'overflow-x-auto rounded-md bg-muted px-3 py-2 text-xs [&_.shiki]:!bg-transparent',
    className,
  );

  return (
    <div className="group relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleCopy}
        aria-label={uiText(copied ? 'common.copied' : 'common.copy')}
        className="absolute top-1.5 right-1.5 size-7 text-muted-foreground opacity-0 transition-opacity hover:bg-accent/15 hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </Button>
      {html === null ? (
        <pre className={preClass}>
          <code>{code}</code>
        </pre>
      ) : (
        // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML gerado pelo shiki a partir de `code` (string técnica própria, não user-content) e já sanitizado — markdown de usuário exigiria rehype-sanitize.
        <div className={preClass} dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </div>
  );
}
